/* eslint-disable no-undef */
import { useEffect, useRef, useState } from "react";
import { getLocalStorageItem, setLocalStorageItem } from "../../utils/storage/localStorageUtil";
import isEmpty from "lodash.isempty";
import { isJsonString } from "../../utils/common";

// Sent when an instance has loaded the A/B variables, so every other mounted instance updates too (a component
// that mounted before CleverTap was ready would otherwise keep the default experience until a refresh).
const AB_TESTING_VIEW_UPDATED_EVENT = "abTestingViewUpdated";
// CleverTap SDK not loaded yet at mount: check again every 500ms, for up to 15s.
const CLEVERTAP_READY_CHECK_INTERVAL_MS = 500;
const CLEVERTAP_READY_MAX_CHECKS = 30;

/**
 * Custom hook to handle A/B Testing View
 * @returns {Object} A/B Key-Value pairs from CleverTap.
 */
export const useABTestingView = () => {
  const [abTestingView, setAbTestingView] = useState(null);
  const [parseData, setParseData] = useState(null);
  const [trigger, setTrigger] = useState(false);
  const fetchVariablesPromise = useRef(null);

  /**
   * Fetch CleverTap variables and process local storage data
   */
  const fetchVariablesHandler = async () => {
    if (!window?.clevertap) return;

    const localStorageKey = "WZRK_PE";
    const cachedData = getLocalStorageItem(localStorageKey);
    const parsedData = cachedData && isJsonString(cachedData) ? JSON.parse(cachedData) : {};
    const expiryTime = parsedData?.expiry;

    // If cached data is valid, resolve immediately
    if (!isEmpty(parsedData?.value) && expiryTime > Date.now()) {
      return Promise.resolve(parsedData.value);
    }

    // If a fetch is already in progress, return the shared promise
    if (fetchVariablesPromise.current) {
      return fetchVariablesPromise.current;
    }

    // Fetch variables from CleverTap if cache is invalid or expired
    fetchVariablesPromise.current = new Promise((resolve, reject) => {
      window.clevertap.fetchVariables((variables, error) => {
        if (error) {
          fetchVariablesPromise.current = null;
          reject(error);
        } else {
          const updatedData = {
            value: getLocalStorageItem(localStorageKey),
            expiry: Date.now() + 30 * 60 * 1000, // 30 minutes expiry
          };
          setLocalStorageItem(localStorageKey, JSON.stringify(updatedData));
          fetchVariablesPromise.current = null;
          resolve(variables);
        }
      });
    });
    return fetchVariablesPromise.current;
  };

  /**
   * Handles fetching CleverTap variables and localStorakge data
   */
  const ABTestingViewHandler = async () => {
    try {
      // Wait for CleverTap variables to be fetched
      await fetchVariablesHandler();

      const cachedData = getLocalStorageItem("WZRK_PE");
      if (!cachedData) return;

      const parsedData = JSON.parse(cachedData);
      if (!parsedData?.value) return;

      const decodedData = JSON.parse(decodeURIComponent(parsedData.value));
      setParseData(decodedData);
    } catch (error) {
      console.error("Error fetching variables or processing data:", error);
    }
  };

  /**
   * This is used to re-fetch the CleverTap variables
   */
  const recallABTestingHook = () => {
    setTrigger((prev) => !prev);
  };

  /**
   * Event listener to trigger ABTestingViewHandler on load
   */
  useEffect(() => {
    if (typeof window !== "undefined" && window?.clevertap) {
      ABTestingViewHandler();
    }
  }, [typeof window !== "undefined" && window?.clevertap, trigger]);

  /**
   * CleverTap not loaded yet when this component mounted (e.g. first visit): fetch once it is available.
   */
  useEffect(() => {
    if (typeof window === "undefined" || window?.clevertap) return undefined;
    let checks = 0;
    const timerId = setInterval(() => {
      checks += 1;
      if (window?.clevertap) {
        clearInterval(timerId);
        recallABTestingHook();
      } else if (checks >= CLEVERTAP_READY_MAX_CHECKS) {
        clearInterval(timerId);
      }
    }, CLEVERTAP_READY_CHECK_INTERVAL_MS);
    return () => clearInterval(timerId);
  }, []);

  /**
   * Picks up the A/B variables loaded by another instance of this hook.
   */
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    /**
     * @param {CustomEvent} event - detail: A/B Key-Value pairs.
     */
    const onAbTestingViewUpdated = (event) => {
      if (isEmpty(event?.detail)) return;
      setAbTestingView((prev) => (prev === event.detail ? prev : { ...(prev || {}), ...event.detail }));
    };
    window.addEventListener(AB_TESTING_VIEW_UPDATED_EVENT, onAbTestingViewUpdated);
    return () => window.removeEventListener(AB_TESTING_VIEW_UPDATED_EVENT, onAbTestingViewUpdated);
  }, []);

  /**
   * Update abTestingView whenever parseData changes
   */
  useEffect(() => {
    if (!isEmpty(parseData)) {
      const dataObj = {};
      for (const key in parseData) {
        dataObj[key] = parseData[key];
        window[key] = parseData[key];
      }
      setAbTestingView(dataObj);
      window.dispatchEvent(new CustomEvent(AB_TESTING_VIEW_UPDATED_EVENT, { detail: dataObj }));
    }
  }, [parseData]);

  return {
    ...(abTestingView || {}),
    recallABTestingHook,
  };
};
